import { type ReactElement } from "react";
import {
	DisplaySelect,
	DisplaySelectValueType,
} from "@/components/Atoms/DisplayField";
import {
	DisplayFieldFeedback,
	DisplayFieldState,
} from "@/components/Atoms/DisplayField/display-field.shared";
import { useFieldFeedbackMap } from "@/components/Atoms/FeedbackIndicator";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { showApiErrorToast } from "@/utils/toast.utils";

const YES = "yes";
const NO = "no";

export interface RequiredLocationFieldProps {
	value: boolean;
	canEdit: boolean;
	onChange: (next: boolean) => Promise<void>;
}

/**
 * Entity-type Details row: Required Location Yes/No.
 * Choosing a value saves immediately. No helper text.
 */
export function RequiredLocationField({
	value,
	canEdit,
	onChange,
}: RequiredLocationFieldProps): ReactElement {
	const { t } = useAppTranslation();
	const { feedbackByField, setFieldFeedback } = useFieldFeedbackMap<"required">();
	const feedback = feedbackByField.required ?? DisplayFieldFeedback.Idle;
	const yesLabel = t("common.actions.yes");
	const noLabel = t("common.actions.no");
	const label = t("admin.entityTypes.drawer.details.requiredLocation");

	const commit = (key: string) => {
		const next = key === YES;
		if (next === value || feedback === DisplayFieldFeedback.Saving) return;
		setFieldFeedback("required", DisplayFieldFeedback.Saving);
		void onChange(next)
			.then(() => {
				setFieldFeedback("required", DisplayFieldFeedback.Saved);
			})
			.catch((error: unknown) => {
				setFieldFeedback("required", DisplayFieldFeedback.Idle);
				showApiErrorToast({
					error,
					message: t("admin.entityTypes.drawer.details.saveFailed"),
				});
			});
	};

	return (
		<div className="w-full" data-testid="entity-type-required-location-field">
			<DisplaySelect
				label={label}
				value={value ? yesLabel : noLabel}
				valueType={DisplaySelectValueType.Text}
				showValueIcon={false}
				state={
					canEdit ? DisplayFieldState.Enabled : DisplayFieldState.Disabled
				}
				feedback={feedback}
				showEdit={canEdit}
				options={
					canEdit
						? [
								{ id: YES, label: yesLabel, testId: "required-location-yes" },
								{ id: NO, label: noLabel, testId: "required-location-no" },
							]
						: undefined
				}
				selectedKey={value ? YES : NO}
				onSelectionChange={commit}
				data-testid="entity-type-required-location-select"
			/>
		</div>
	);
}
